import { z } from 'zod';
import { TaskIdSchema } from './ids';

/**
 * Run-less events a box reports about ITSELF, on `fleet.box.<runnerId>.<kind>`.
 *
 * Distinct from `FleetRunEvent` (`fleet.evt.<runId>.<kind>`) in the one way that
 * matters: these are published by a runner that has NOT started work and may never.
 * The boot refusal is the first — a box whose config gate fired exits before it ever
 * claims a task, so it has no `run_id` to key an event on (#1917).
 */

/** The box-level event kinds. The subject taxonomy allows more. */
export const BoxEventKind = {
  /** A boot-time config guard refused to start this build on this box. */
  BootRefused: 'boot_refused',
  /** The box shipped a bounded slice of its OWN process log to object storage. */
  RunnerLog: 'runner_log',
  /** One hop of a self-update the box made on its own — a pull, a swap, a boot verdict. */
  Update: 'update',
  /** What one credential roll concluded — the half of re-issue the API never sees. */
  CredsRefresh: 'creds_refresh',
  /** The box pulled an assignment it could not decode, reported BEFORE it drops it (plan 101 F4). */
  AssignmentUndecodable: 'assignment_undecodable',
} as const;
export type BoxEventKind = (typeof BoxEventKind)[keyof typeof BoxEventKind];

export const BoxEventKindSchema = z.enum(BoxEventKind);

/**
 * The CLOSED vocabulary of boot-refusal reason ids a box may report (#2223).
 *
 * The id is the whole interface between a guard and the mothership's judgement:
 * `attributeRefusal` looks it up to decide whether a refusal is the box's own fault
 * or the release's, and an id the table does not classify convicts the release. So
 * the string a box sends is load-bearing, and until this set existed it was
 * `z.string().min(1)` at every seam it crossed.
 *
 * Each entry names the guard that emits it, so adding one is a decision made with
 * the guard's semantics in hand rather than a string invented at a call site.
 */
export const RefusalReasonId = {
  /** `apps/runner/src/identity-assertion.ts` — GH_TOKEN/GITHUB_TOKEN instead of an installation token. */
  BotTokenPersonalCreds: 'bot_token.personal_creds',
  /**
   * `apps/runner/src/modes/worker/team/session/boot.ts` — built-in harness selected, never
   * configured.
   */
  TeamHarnessUnconfigured: 'team.harness_unconfigured',
  /**
   * `apps/runner/src/creds-identity.ts` — the held `nats.creds` grant names a runner
   * other than `DZ_RUNNER_ID`, so the box would speak on the fleet plane as a box it
   * is not. That file's `CREDS_RUNNER_MISMATCH_REASON` is this same string.
   */
  NatsCredsRunnerMismatch: 'nats_creds.runner_mismatch',
  /**
   * Sent by a box build that refuses to boot on a malformed HYGIENE knob in its own env
   * (`DZ_HYGIENE_MIN_FREE_GB`, say) rather than reading it as a default mid-run. A build
   * with no such boot gate never sends it.
   */
  ConfigHygieneKnob: 'config.hygiene_knob',
  /**
   * Sent by a box build that refuses to boot on one of its other malformed numeric knobs,
   * at the same boot gate. The box's own env, like the entry above: the same build boots
   * on a box whose number parses.
   */
  ConfigBoxKnob: 'config.box_knob',
} as const;
export type RefusalReasonId = (typeof RefusalReasonId)[keyof typeof RefusalReasonId];

export const REFUSAL_REASON_IDS: readonly string[] = Object.values(RefusalReasonId);

/**
 * Is this reason id one the platform knows?
 *
 * Deliberately a PREDICATE and not a schema refinement. An unknown id must still
 * reach the rollout controller and still count against the release — that is the
 * fail-closed rule a release shipping a broken new guard depends on (#1917). Parsing
 * it away at the wire would be a silent drop dressed up as validation: the refusal
 * would vanish, the box would look silent, and the build would never be convicted.
 *
 * So the wire stays permissive and the ANSWER is recorded instead — see
 * `handleBoxEvent`, which files `reason_known` beside the raw reason.
 */
export function isKnownRefusalReason(reason: string): reason is RefusalReasonId {
  return (REFUSAL_REASON_IDS as readonly string[]).includes(reason);
}

/**
 * A box's report that it deliberately refused to run a build.
 *
 * **There is no `runner_id` field, and that is the design.** The publishing box's
 * identity comes from the SUBJECT — `fleet.box.<runnerId>.<kind>` — whose publish
 * grant `runnerSubjectScope` pins to that one id, so the wire proves who spoke. A
 * `runner_id` in the body would be a self-asserted claim sitting next to a proven
 * one, and the first time the two disagreed someone would have to decide which
 * wins. Omitting it means the sink has exactly one place to read identity from.
 *
 * Likewise there is no authoritative timestamp here. `observed_at` is the BOX's
 * clock and is diagnostic only: the mothership's attribution orders refusals in
 * time (`attributeRefusal` requires a prior refusal to be EARLIER), so letting a
 * box supply that ordering would let a skewed — or lying — clock decide whether its
 * own refusal is excused. The sink stamps the authoritative time from the server.
 */
export const BootRefusedEventSchema = z.object({
  kind: z.literal(BoxEventKind.BootRefused),
  /** The build the box refused to start. */
  version: z.string().min(1),
  /**
   * The guard that refused, as a STABLE machine-readable id (`bot_token.personal_creds`),
   * never a human sentence. Attribution compares reasons across releases, so free
   * text here would make every refusal look like a fresh fault and nothing would
   * ever be attributed to the box.
   */
  reason: z.string().min(1),
  /** The build the box was on before the swap, when it knows it. Diagnostic. */
  from_version: z.string().min(1).nullable().optional(),
  /** The box's own clock at refusal, ISO-8601. DIAGNOSTIC ONLY — see above. */
  observed_at: z.string().min(1).optional(),
});
export type BootRefusedEvent = z.infer<typeof BootRefusedEventSchema>;

// ─── runner log egress ───────────────────────────────────────────────────────
//
// A box's own stdout is the one thing the platform could never see. The runner's
// seven module-local loggers write JSON to journald and stop there, so a box that
// misbehaves WITHOUT a run — a dead work loop under a live heartbeat, a boot that
// wedges after presence — leaves nothing to read. The task run-event stream is the
// only observability there is, and it is keyed on a run that never existed.
//
// This event is the POINTER half of the CI-log shape (`ci_jobs.log_ref` +
// `ci_jobs.log_excerpt`): the bytes go to R2 under the `logs/runner/<id>/` prefix
// (deleted after `RUNNER_LOG_RETENTION_DAYS` by `cron.prune_runner_logs`, #6422 — no bucket
// lifecycle rule exists, plan 101 06-2), and the wire carries a key plus a `<2KB`
// tail. That split is load-bearing rather than stylistic in TWO places:
//
//   - `audit_events` is a tamper-evident hash chain no age prune touches on purpose
//     (`cron.audit_archive` archives it after 90 days; nothing deletes it from the
//     record). One row per TRIGGER, never per line — a log routed into rows would grow a
//     table that is only ever archived, never trimmed.
//   - `BOX_EVENTS_STREAM_MAX_MSG_BYTES` is 64 KB precisely because "nothing here
//     carries a diff or a log". The pointer keeps that true.

/**
 * Why a box shipped its log. **Display-only, and that is a rule, not a habit.**
 *
 * Contrast `BootRefusedEventSchema.reason` directly above: that string is a stable
 * machine id BECAUSE it is a decision key — the rollout controller compares reasons
 * across releases to decide whether a release or a box takes the blame. This one is
 * the opposite. Nothing may key a grader, a refusal table, or a rollout attribution
 * on a runner log: the bytes are unverified box narration, and a box that could
 * change a verdict by choosing what to print would be deciding its own case.
 */
export const RunnerLogTrigger = {
  /** The mode loop stopped pulling and settled (an operator drain, a self-update swap). */
  Drain: 'drain',
  /** A per-run guard fired and killed the run (`guard_tripped`). */
  GuardTrip: 'guard_trip',
  /** A run reached a terminal state that was not `completed`. */
  RunFailed: 'run_failed',
  /** The supervisor asked the process to stop. */
  Sigterm: 'sigterm',
  /**
   * The self-update path refused, aborted or undid an install (plan 101 runner
   * lifecycle, slice 06). Before this trigger existed the cause of a box that had
   * silently stopped converging — a root-owned binary directory, a downgrade the
   * alias walked it into, a crash-loop pin — lived only in that box's journal.
   */
  UpdateFault: 'update_fault',
  /**
   * The box's work loop stopped being able to pull and handed itself back to the
   * supervisor (`apps/runner/src/pull-fault.ts`). Its own member, and not a shade of
   * {@link RunnerLogTrigger.RunFailed}, because it competes with one: the pull ladder
   * ships an early warning at rung 5 and this VERDICT at the ceiling ~25 s later, and a
   * shared trigger means the first stamps the egress marker and the second — the ending
   * an operator actually needs — is rate-limited away every time (#4075).
   *
   * Adding a member here is NOT a rollout-ordered change, because
   * {@link RunnerLogTriggerSchema} is an open string — see it for why that was chosen over
   * sequencing two clocks nobody owns. This list stays the producer's vocabulary and the
   * renderer's; the wire admits anything and {@link isKnownRunnerLogTrigger} says whether
   * this build recognises it.
   */
  WorkLoopStalled: 'work_loop_stalled',
} as const;
export type RunnerLogTrigger = (typeof RunnerLogTrigger)[keyof typeof RunnerLogTrigger];

/**
 * Ceiling on a trigger id. OPEN is not UNBOUNDED: every other box-authored string on this
 * event is capped (`log_ref` at 256 chars, `excerpt` at 2 048 BYTES), and this one lands
 * in `audit_events`, the append-only hash chain no age prune touches (`cron.audit_archive`
 * archives it after 90 days; nothing deletes it from the record).
 *
 * The property, not a multiple: comfortably past the longest member this build names
 * (`work_loop_stalled`, 17 chars) with room for a more descriptive one, and far short of
 * anything that could matter in a row the record keeps forever. What this bounds is the AUDIT ROW,
 * not the enum — the enum is open by design, so the cap has to hold for ids this build
 * has never seen.
 */
export const RUNNER_LOG_TRIGGER_MAX_CHARS = 64;

/**
 * The wire type for a runner log's `trigger` — an OPEN string, matching
 * {@link UpdateBoxEventSchema}'s `reason` and for word-for-word the same reason: nothing
 * downstream branches on it, so a box on a build this mothership has never heard of must
 * still produce a readable row rather than a rejected event.
 *
 * **Why open rather than rollout-ordered (#4075 review).** A closed `z.enum` here makes
 * every future member a sequencing problem, and the sequencing argument is weaker than it
 * first looks: the box's own mothership-facing decoder (`box-event-sink.ts`, a
 * `discriminatedUnion` — the only strict parser on this lane) rejects the WHOLE event on
 * an unknown member, and the two clocks that would have to stay ordered are not one. The
 * pull path is up to 75 min (`update-check-arming.ts`), but the PUSH path is
 * `cron.fleet_autoroll` at 15 min from publish, and `release.yml` builds the runner
 * binary and the worker image as SIBLINGS (`needs: changes`) with argocd-image-updater as
 * a third independent clock. The margin is real but small and nobody owns it. An open
 * string makes the race unrepresentable instead of unlikely, at the cost of a display
 * string a reader may not recognise — and {@link isKnownRunnerLogTrigger} is how that is
 * answered, exactly as `isKnownRefusalReason` answers it for a refusal id.
 */
export const RunnerLogTriggerSchema = z.string().min(1).max(RUNNER_LOG_TRIGGER_MAX_CHARS);

/**
 * Is this trigger one this build knows how to render?
 *
 * A PREDICATE, not a schema refinement, for the reason {@link isKnownRefusalReason} gives:
 * parsing an unknown value away at the wire is a silent drop dressed as validation. Here
 * the stakes are only display, which is precisely why the wire can afford to be
 * permissive.
 */
export function isKnownRunnerLogTrigger(trigger: string): trigger is RunnerLogTrigger {
  return (Object.values(RunnerLogTrigger) as string[]).includes(trigger);
}

/**
 * Inline tail carried on the wire — the same `<2KB` budget `ci_jobs.log_excerpt`
 * holds (`@developerz/storage` `LOG_EXCERPT_MAX_BYTES`), so an operator sees why a
 * box died without fetching the archive.
 */
export const RUNNER_LOG_EXCERPT_MAX_BYTES = 2048;

const utf8 = new TextEncoder();

/**
 * A box's report that it archived a slice of its own process log.
 *
 * **No `runner_id`, exactly as {@link BootRefusedEventSchema} has none**, and here
 * the omission buys something sharper than tidiness: a BYOVM box is hardware someone
 * else owns. If the body named its own author, a hostile box could file a log
 * "from" a neighbour — and a log is read by a human diagnosing that neighbour.
 * Identity comes from the SUBJECT and only the subject.
 *
 * `log_ref` is the one field that is a POINTER rather than a fact, so it is the one
 * field a sink must not take at face value. It was minted server-side by the presign
 * responder, but it round-trips through the box to get here, and a presigned key that
 * came back rewritten would aim a later presigned GET at whatever the box named.
 * The sink pins it to `logs/runner/<subject runner id>/` before it lands; see
 * `apps/worker/src/fleet/box-event-sink.ts`.
 */
export const RunnerLogEventSchema = z.object({
  kind: z.literal(BoxEventKind.RunnerLog),
  /**
   * Object key of the archived (zstd) log. Bounded and prefix-checked at the sink —
   * never trusted as written. Keep this in step with `runnerLogKey`.
   */
  log_ref: z.string().min(1).max(256),
  /** Raw (uncompressed) bytes the ring held when it was drained. */
  bytes: z.number().int().nonnegative(),
  /** Lines the ring held. */
  lines: z.number().int().nonnegative(),
  /**
   * Lines the ring EVICTED before this egress — overflow plus any payload the
   * secret-shape guard refused. Non-zero means the archive is a suffix, not the
   * whole story, and an operator reading a truncated log has to know that.
   */
  dropped: z.number().int().nonnegative(),
  trigger: RunnerLogTriggerSchema,
  /**
   * The log's tail, scrubbed box-side. Bounded in UTF-8 BYTES rather than by
   * `z.string().max()`, which counts UTF-16 units: a 2048-unit string of
   * astral-plane characters is 8 KB on the wire, and this schema is the trust
   * boundary against a box that did not use `tailExcerpt`.
   */
  excerpt: z
    .string()
    .refine(
      (s) => utf8.encode(s).length <= RUNNER_LOG_EXCERPT_MAX_BYTES,
      `excerpt must be at most ${RUNNER_LOG_EXCERPT_MAX_BYTES} UTF-8 bytes`,
    ),
  /** The box's own clock at egress, ISO-8601. DIAGNOSTIC ONLY — the sink stamps the real one. */
  observed_at: z.string().min(1).optional(),
});
export type RunnerLogEvent = z.infer<typeof RunnerLogEventSchema>;

// ─── self-update hops the box makes on its own ────────────────────────────────
//
// The mothership's rollout controller audits every hop of a PUSH — `published`,
// `confirmed`, `failed`, `rolled_back` — because it decided each one. The box's own
// half of self-update decided nothing the mothership could see: a pull it armed from
// the channel alias, a swap that landed, a build it verified at boot, a build it
// rolled back after repeated failed boots — every one of those was a `console.log`
// line on the box and nothing else. Measured 2026-09-02: 2,250 `runner.update_published`
// rows, 2,111 `runner.update_confirmed`, and ZERO rows for any pull-side install, while
// the boxes' journals showed 4-10 pulls per box per 48h (plan 101 runner lifecycle,
// slice 06). `readReleaseInstallEvidence` — the condemn decision — was therefore blind
// to every install the push had missed.
//
// This event is the box's testimony for those hops. Same posture as the two events
// above: no `runner_id` (the subject proves who spoke), `observed_at` diagnostic only.

/**
 * Which hop of the box's own self-update this reports. These are the box's OWN
 * structured `decision` ids (`apps/runner/src/update-check/check.ts`,
 * `apps/runner/src/self-update.ts`, `apps/runner/src/update-boot/lifecycle.ts`),
 * carried verbatim so the audit row and the box's journal name the same thing.
 */
export const UpdateBoxDecision = {
  /** The pre-work check armed an install of `version` from the channel (`update-check/check.ts`). */
  PullStarted: 'update_pull_started',
  /** The verified bytes are on disk over the binary; the box is about to exit into them. */
  Swapped: 'swapped',
  /** A swapped build reached health (NATS connected + first presence beat) — `update-boot/lifecycle.ts`. */
  BootVerified: 'update_boot_verified',
  /** Repeated failed boots on `version` restored the preserved binary — `update-boot/lifecycle.ts`. */
  RolledBack: 'update_rolled_back',
  /**
   * The install did not complete, and `reason` says which of three ways — they are
   * different faults with different operator actions:
   *
   *   - `rollback_unprepared` — the preserve-and-stamp hook threw, so the box REFUSED a
   *     build it could not roll back from. The NEW binary never landed; the OUTGOING one
   *     very likely did, in whole or in part. That hook is
   *     `stampPendingVerification`, whose first act is `store.preserve()` — a ~98 MB
   *     `copyFile` to `<binary>.prev.<uuid>.tmp`, then a rename to `<binary>.prev`
   *     (`apps/runner/src/update-boot/lifecycle.ts`; the `store.preserve()` port it
   *     calls is `apps/runner/src/update-boot/store.ts`). So a throw INSIDE the copy
   *     (ENOSPC is the measured one) leaves the temp, and a throw in the marker write
   *     AFTER it leaves a complete `<binary>.prev`. The temp is swept at the next boot
   *     (`isStalePreserveTemp`, #3004); the `.prev` is deliberately NOT, because that is
   *     the file a rollback restores from. An operator reading this hop on a full disk
   *     should go LOOKING for those bytes.
   *   - `install_faulted` — the install threw past its own graded failures after the box
   *     had already QUIESCED (#4074). The box exits 1 on this rather than going on
   *     beating `draining: true` and pulling nothing, so the hop is also the only
   *     countable trace of a state the presence KV's `capabilities` jsonb cannot rank.
   *     Nearly always before the swap, but not promised: the throw it catches could be
   *     the `swapped` log line itself.
   *   - `swap_failed` — writing or renaming the verified bytes over the binary threw
   *     (`apps/runner/src/self-update.ts` `refuseSwap`, #6422). The box stays on the build
   *     it was running and reports the hop instead of going silent; until it did, the
   *     mothership saw nothing and graded the box `confirm_deadline` ten minutes later.
   *
   * NO arm promises that no byte landed. This used to read "the swap was refused
   * before any byte landed" and that was false for both of them — most sharply for the
   * arm whose own failure mode is running out of room mid-copy.
   */
  SwapAborted: 'swap_aborted',
  /**
   * The box could not FETCH or VERIFY the build it was told to take, so nothing was
   * swapped and it stays on the build it runs (#7083 defect D). `reason` is one of
   * {@link UpdateFetchFailedReason}. Before this hop the box logged `verify_failed`,
   * returned, and said nothing upstream; the controller then graded a live, silent box
   * `repush_unanswered` — a build fault — for what was a missing or unreadable artifact.
   * A disk failure on the way (`insufficient_disk`, `disk_check_failed`) is NOT this hop:
   * it is {@link UpdateBoxDecision.SwapAborted}.
   */
  FetchFailed: 'update_fetch_failed',
  /**
   * The box holds a live run and is waiting it out before swapping (`self-update.ts`,
   * the busy branch). Carries `wait_ceiling_ms`, always `null` since 2026-09-03: no
   * run is killed by a wall clock any more, so the wait is always unbounded — see
   * that field's own docblock. Until this was a hop the deferral was a journal line
   * on the box, the mothership saw silence and graded the box `confirm_deadline` at
   * 10 min — 56 such rows on prod by 2026-09-03, not one of them ever followed by a
   * confirm for the same (box, version).
   */
  Deferred: 'update_deferred',
  /**
   * The deferred wait ended and the swap proceeds — always because the run finished
   * on its own terms; `self-update.ts` never drains it itself, so `reason` is always
   * absent.
   */
  DeferredSettled: 'update_deferred_settled',
} as const;
export type UpdateBoxDecision = (typeof UpdateBoxDecision)[keyof typeof UpdateBoxDecision];

export const UpdateBoxDecisionSchema = z.enum(UpdateBoxDecision);

/**
 * Why an `update_fetch_failed` hop failed — the box's `reason` on that decision. Three
 * faults with three different owners, which is why the controller grades each apart
 * (`apps/worker/src/fleet/rollout-repush.ts` `deadlineFailureReason`):
 *
 *   - `artifact_unavailable` — the bucket ANSWERED that the object is not there (HTTP 404
 *     or 410; the box's grading is `apps/runner/src/self-update-fetch-failure.ts`
 *     `gradeArtifactStatus`). A LEDGER or bucket fault — retention pruned bytes the ledger
 *     still offers — never a fault of the build, so it never counts toward a halt and
 *     never condemns. A manifest naming no artifact for the box's target is NOT this
 *     reason: that is the pull half's `no_artifact`, which the box does not report here.
 *   - `fetch_error` — no answer the box can trust: the network, a timeout, any other HTTP
 *     status, a body shorter than its `Content-Length`, a `.sig` of the wrong length.
 *     Transient: uncounted, and the controller re-pushes once.
 *   - `signature_invalid` — the bytes arrived whole and no fleet key verified them. ONE
 *     box's word (its keys come from its own env), so it is graded on that box alone; the
 *     controller halts the pass only when two distinct boxes report it and no box has
 *     verified the build (`apps/worker/src/fleet/rollout-halt-verdict.ts`).
 *
 * The wire field stays the OPEN `reason` string ({@link UpdateBoxEventSchema}): a box one
 * release ahead naming a reason this build has never heard of still lands a readable row.
 * The mothership classifies through {@link UpdateFetchFailedReasonSchema}, and a reason it
 * does not recognise is NOT one of these grades (the hop only proves the box heard the push).
 */
export const UpdateFetchFailedReason = {
  ArtifactUnavailable: 'artifact_unavailable',
  FetchError: 'fetch_error',
  SignatureInvalid: 'signature_invalid',
} as const;
export type UpdateFetchFailedReason =
  (typeof UpdateFetchFailedReason)[keyof typeof UpdateFetchFailedReason];

export const UpdateFetchFailedReasonSchema = z.enum(UpdateFetchFailedReason);

/**
 * Which half of self-update produced the hop. `pull` is the box's own pre-work check
 * against the channel alias; `push` is a `fleet.update.<runnerId>` message from the
 * mothership (its rollback of a halted build is a push too — it arrives as one);
 * `rollback` is the box's own boot-time restore of the preserved binary.
 */
export const UpdateSourceSchema = z.enum(['pull', 'push', 'rollback']);
export type UpdateSource = z.infer<typeof UpdateSourceSchema>;

/**
 * One self-update hop the box made on its own.
 *
 * `version` is the build the hop is ABOUT — the one being pulled, swapped, verified
 * or rolled back — and `from_version` the build the box ran before it, when the box
 * knows (a boot cannot always date the binary it restored). Named to match the audit
 * rows the rollout controller writes (`version` / `fromVersion`), so one query over
 * `audit_events.input->>'version'` reads the push's evidence and the pull's alike.
 */
export const UpdateBoxEventSchema = z.object({
  kind: z.literal(BoxEventKind.Update),
  decision: UpdateBoxDecisionSchema,
  version: z.string().min(1),
  from_version: z.string().min(1).nullable(),
  source: UpdateSourceSchema,
  /**
   * The hop's own reason id — on `swap_aborted`, `rollback_unprepared`, `install_faulted`
   * or `swap_failed` (see {@link UpdateBoxDecision.SwapAborted}); on `update_fetch_failed`,
   * one of {@link UpdateFetchFailedReason}.
   *
   * An open string rather than an enum, and deliberately: the SINK never branches on it.
   * It maps the audit row from `decision` alone
   * (`apps/worker/src/fleet/box-event-sink.ts` `updateDecisionToAuditKind`), so this is
   * display and grouping — a box on an older build naming a reason this one has never
   * heard of must still produce a readable row, not a rejected event. The one reader that
   * branches is the rollout grader, on `update_fetch_failed` only, and it parses through
   * {@link UpdateFetchFailedReasonSchema} rather than trusting the string.
   */
  reason: z.string().min(1).optional(),
  /**
   * On `update_deferred` only: how long the box will wait for its run before it
   * installs anyway. Always `null` since 2026-09-03 — no lane kills a run by
   * elapsed time any more (`guards.ts`), so a deferred self-update waits for the
   * run's own progress-liveness guards to resolve it rather than for a wall clock
   * of its own. The field stays nullable rather than deleted: it is still read by
   * the rollout grader's confirm-deadline extension, and `null` there already means
   * "the wait is unbounded" — which is now the only value this ever carries.
   */
  wait_ceiling_ms: z.number().int().positive().nullable().optional(),
  /** The box's own clock, ISO-8601. DIAGNOSTIC ONLY — the sink stamps the real one. */
  observed_at: z.string().min(1).optional(),
});
export type UpdateBoxEvent = z.infer<typeof UpdateBoxEventSchema>;

// ─── credential roll outcomes (plan 101 runner lifecycle, doc 05 #5) ──────────
//
// The API writes `runner.creds_reissued` when it MINTS. Every way a roll fails
// happens on the box, after or instead of that mint, and until this event each was
// a journal line nobody reads: the control plane was down (`error`), the mothership
// refused (`refused`), the disk was read-only (`write_blocked`), or the credential
// that came back did not outlive the one held (#2732, `reissue_stale`). The
// `box_creds_lapsed` alert keys on the API's own `nats_creds_expires_at`, which a
// lost or stale response leaves FRESH for a box that is in fact about to lapse —
// so the box has to say what actually happened.

/**
 * What one roll concluded, as the box reports it. Mirrors the box's own
 * `CredsRefreshOutcome` minus the two that leave nothing to report: `current`
 * (nothing asked) and `not_configured` (a dev box with no token, alerted at boot).
 */
export const CredsRefreshOutcomeSchema = z.enum([
  'refreshed',
  'refused',
  'error',
  'write_blocked',
  'reissue_stale',
]);
export type CredsRefreshReportOutcome = z.infer<typeof CredsRefreshOutcomeSchema>;

export const CredsRefreshBoxEventSchema = z.object({
  kind: z.literal(BoxEventKind.CredsRefresh),
  outcome: CredsRefreshOutcomeSchema,
  /** `exp` of the credential the box HELD going in, ISO-8601; null when unreadable. */
  held_expires_at: z.string().min(1).nullable(),
  /** `exp` of the credential the mothership handed back, when it did; null otherwise. */
  minted_expires_at: z.string().min(1).nullable(),
  /** The HTTP status the API answered, when it answered. */
  status: z.number().int().optional(),
  /** Bounded error text on `error` / `write_blocked`. Metadata only — never a credential. */
  error: z.string().max(512).optional(),
  /** The box's own clock, ISO-8601. DIAGNOSTIC ONLY — the sink stamps the real one. */
  observed_at: z.string().min(1).optional(),
});
export type CredsRefreshBoxEvent = z.infer<typeof CredsRefreshBoxEventSchema>;

// ─── undecodable assignment (plan 101 F4) ─────────────────────────────────────
//
// `pullNextTask` used to `term()` a push the box could not decode and say nothing
// upstream: the task stayed `assigned` until `stuck-task.ts` re-pended it at 180 min,
// then the assigner handed the SAME bytes to the same box and it dropped them again.
// The measured trigger is a mixed-version rollout — a mothership that learned one
// new enum member before the box did fails the whole assignment. This event is the
// box's report, published BEFORE the drop (`pullNextAssignment`,
// `packages/nats/src/consumers.ts`), so the mothership can re-pend at once.

/** Ceiling on the decoder's error text — it lands in `audit_events`, archived after 90 days (`cron.audit_archive`), never deleted from the record. */
export const ASSIGNMENT_UNDECODABLE_ERROR_MAX_CHARS = 512;

/**
 * A box's report that it pulled an assignment it could not decode.
 *
 * No `runner_id`, like every box event: the subject proves who spoke. The three
 * references are SALVAGED from bytes the full schema refused
 * ({@link salvageAssignmentRefs}), so each is `null` when it could not be proven,
 * and every one is a box-authored CLAIM. The mothership must re-pend only a task
 * that is assigned to THIS subject's runner at THIS `lease_epoch` — a claim about
 * another box's task, or an epoch the task has moved past, changes nothing.
 */
export const AssignmentUndecodableEventSchema = z.object({
  kind: z.literal(BoxEventKind.AssignmentUndecodable),
  /** JetStream sequence of the dropped push on FLEET_TASKS — names WHICH push, even with no ids. */
  stream_seq: z.number().int().positive(),
  /** The coding task the push was for, when its `task_id` survived. */
  task_id: TaskIdSchema.nullable(),
  /** The CI job the push was for, when it was a `ci_dispatch` whose id survived. */
  ci_job_id: z.string().uuid().nullable(),
  /** The claim's fence token, when it survived — the mothership's CAS key for the re-pend. */
  lease_epoch: z.number().int().nonnegative().nullable(),
  /** The decoder's own error text, truncated box-side. Display only — never a decision key. */
  error: z.string().min(1).max(ASSIGNMENT_UNDECODABLE_ERROR_MAX_CHARS),
  /** The box's own clock, ISO-8601. DIAGNOSTIC ONLY — the sink stamps the real one. */
  observed_at: z.string().min(1).optional(),
});
export type AssignmentUndecodableEvent = z.infer<typeof AssignmentUndecodableEventSchema>;

/** The references {@link salvageAssignmentRefs} could prove, each independently. */
export type SalvagedAssignmentRefs = Pick<
  AssignmentUndecodableEvent,
  'task_id' | 'ci_job_id' | 'lease_epoch'
>;

/**
 * Recover what a refused assignment still proves about itself. Each field is
 * validated ON ITS OWN against the same schema the full assignment uses, so one
 * unknown enum member elsewhere does not cost the ids beside it — and a field that
 * does not validate is `null`, never a guess.
 */
export function salvageAssignmentRefs(raw: unknown): SalvagedAssignmentRefs {
  const body: Record<string, unknown> =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const fields = AssignmentUndecodableEventSchema.shape;
  const pick = <T>(schema: z.ZodType<T>, value: unknown): T | null => {
    const parsed = schema.safeParse(value);
    return parsed.success ? parsed.data : null;
  };
  return {
    task_id: pick(fields.task_id, body['task_id']),
    ci_job_id: pick(fields.ci_job_id, body['ci_job_id']),
    lease_epoch: pick(fields.lease_epoch, body['lease_epoch']),
  };
}

/** Every box-event body, discriminated on `kind`. */
export const FleetBoxEventSchema = z.discriminatedUnion('kind', [
  BootRefusedEventSchema,
  RunnerLogEventSchema,
  UpdateBoxEventSchema,
  CredsRefreshBoxEventSchema,
  AssignmentUndecodableEventSchema,
]);
export type FleetBoxEvent = z.infer<typeof FleetBoxEventSchema>;
